<template>
    <div class="quiz">
        <div class="quiz__inner">
            <h2 class="quiz__header heading-secondary">
                {{ title }}
            </h2>
            <div class="container">
                <component
                    :is="currentScreen"
                    :question="currentQuestion"
                    :answers="currentAnswerKeys"
                    :showBackButton="currentScreenIndex > 1 && currentScreenIndex !== 6"
                    :totalPages="totalPages"
                    :currentPage="currentScreenIndex"
                    @updateInLocalStorage="updateInLocalStorage"
                    @next="handleNext"
                    @back="handleBack"
                    @restart="resetQuiz"
                />
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

import RadioScreen from './RadioScreen.vue';
import Screen5 from './Screen5.vue';
import Screen6 from './Screen6.vue';
import Screen7 from './Screen7.vue';
import getPageLang from '../../utils/getPageLang';
import { collectCounters } from '../../utils/countersHelper';

export const QUIZ_STATE_KEY = 'umnico-quiz-state';

export default {
    name: 'Quiz',
    components: {
        RadioScreen,
        Screen5,
        Screen6,
        Screen7,
    },
    props: {
        title: {
            type: String,
            default() {
                return this.$t('__quiz_title');
            },
        },
    },
    data() {
        return {
            currentScreenIndex: 1,
            questions: [
                this.$t('__quiz_screen_1_question'),
                this.$t('__quiz_screen_2_question'),
                this.$t('__quiz_screen_3_question'),
                this.$t('__quiz_screen_4_question'),
                this.$t('__quiz_screen_5_question'),
                this.$t('__quiz_screen_6_question'),
            ],
            answerKeys: [
                [
                    '__quiz_screen_1_answer_1',
                    '__quiz_screen_1_answer_2',
                    '__quiz_screen_1_answer_3',
                    '__quiz_screen_1_answer_4',
                    '__quiz_screen_1_answer_5',
                    '__quiz_screen_1_answer_6',
                ],
                [
                    '__quiz_screen_2_answer_1',
                    '__quiz_screen_2_answer_2',
                    '__quiz_screen_2_answer_3',
                    '__quiz_screen_2_answer_4',
                    '__quiz_screen_2_answer_5',
                ],
                [
                    '__quiz_screen_3_answer_1',
                    '__quiz_screen_3_answer_2',
                    '__quiz_screen_3_answer_3',
                    '__quiz_screen_3_answer_4',
                    '__quiz_screen_3_answer_5',
                ],
                [
                    '__quiz_screen_4_answer_1',
                    '__quiz_screen_4_answer_2',
                    '__quiz_screen_4_answer_3',
                    '__quiz_screen_4_answer_4',
                    '__quiz_screen_4_answer_5',
                ],
                [
                    '__quiz_screen_5_answer_1',
                    '__quiz_screen_5_answer_2',
                    '__quiz_screen_5_answer_3',
                    '__quiz_screen_5_answer_4',
                    '__quiz_screen_5_answer_5',
                    '__quiz_screen_5_answer_6',
                    '__quiz_screen_5_answer_7',
                    '__quiz_screen_5_answer_8',
                ],
            ],
            userAnswers: [],
        };
    },
    computed: {
        currentScreen() {
            if (this.currentScreenIndex <= 4) {
                return 'RadioScreen';
            }
            if (this.currentScreenIndex === 5) {
                return 'Screen5';
            }
            if (this.currentScreenIndex === 6) {
                return 'Screen6';
            }
            return 'Screen7';
        },
        totalPages() {
            return this.questions.length;
        },
        currentQuestion() {
            return this.questions[this.currentScreenIndex - 1];
        },
        currentAnswerKeys() {
            return this.answerKeys[this.currentScreenIndex - 1];
        },
    },
    created() {
        const savedState = JSON.parse(localStorage.getItem(QUIZ_STATE_KEY));
        if (savedState) {
            this.currentScreenIndex = savedState.currentScreenIndex;
            this.userAnswers = savedState.userAnswers;
            this.isDataSent = savedState.isDataSent;
        }
    },
    methods: {
        handleNext(selectedAnswer) {
            this.updateInLocalStorage(selectedAnswer, true);

            if (this.currentScreenIndex < 6) {
                this.currentScreenIndex++;
            } else if (this.currentScreenIndex === 6) {
                this.sendDataToCRM();
            }
        },

        updateInLocalStorage(selectedAnswer, isNext) {
            this.$set(this.userAnswers, this.currentScreenIndex - 1, selectedAnswer);
            const nextPage = isNext ? 1 : 0;

            const quizState = {
                currentScreenIndex: this.currentScreenIndex + nextPage,
                userAnswers: this.userAnswers,
                isDataSent: this.isDataSent,
            };

            localStorage.setItem(QUIZ_STATE_KEY, JSON.stringify(quizState));
        },

        handleBack() {
            if (this.currentScreenIndex > 1) {
                this.currentScreenIndex--;
            }
        },

        async sendDataToCRM() {
            /* eslint-disable camelcase */
            const { utm_source, utm_medium, utm_campaign, utm_term } = JSON.parse(localStorage.umnico_utm || '{}');
            const { googleClientId, roistatVisit, yandexClientId } = collectCounters();
            const data = {
                client: 'mastinvest',
                type: this.getInquiryType(),
                name: this.userAnswers[5]?.name || '',
                phone: this.userAnswers[5]?.phone || '',
                mail: this.userAnswers[5]?.email || '',
                business_type: this.$t(this.userAnswers[0]) || '',
                team_size: this.$t(this.userAnswers[1]) || '',
                role: this.$t(this.userAnswers[2]) || '',
                volume: this.$t(this.userAnswers[3]) || '',
                channels:
                    Array.isArray(this.userAnswers[4]) && this.userAnswers[4].length > 0
                        ? this.userAnswers[4].map((channel) => this.$t(channel)).join('; ')
                        : '',
                page_url: window.location.href,
                utm_source,
                utm_medium,
                utm_campaign,
                utm_term,
                roistat: roistatVisit,
                yclid: yandexClientId,
                gclid: googleClientId,
            };

            await axios
                .post('https://wdg.biz-crm.ru/inserv/in.php', data, {
                    headers: {
                        'Content-Type': 'text/plain',
                    },
                })
                .then(() => {
                    this.isDataSent = true;
                    this.currentScreenIndex = 7;
                    this.name = '';
                    this.phone = '';
                    this.email = '';

                    localStorage.removeItem(QUIZ_STATE_KEY);
                });
        },

        getInquiryType() {
            const pageLang = getPageLang();
            const types = {
                ru: 'web_inquiry_ru',
                en: 'web_inquiry_en',
                es: 'web_inquiry_es',
                de: 'web_inquiry_de',
                pt: 'web_inquiry_pt',
            };
            return types[pageLang] || 'web_inquiry_ru';
        },

        resetQuiz() {
            this.currentScreenIndex = 1;
            this.userAnswers = [];
            this.isDataSent = false;

            localStorage.removeItem(QUIZ_STATE_KEY);
        },
    },
};
</script>

<style lang="scss" src="./style.scss"></style>
